import { useEffect, useMemo } from 'react';

import { DATADOG_ACTIONS } from '../constants';
import { findLocalisation } from '../helpers';

/**
 * Resolves a localisation entry for the given locale, memoising the lookup and
 * reporting a single DataDog warning when the locale cannot be found.
 *
 * Why: callers used to invoke `findLocalisation` inline on every render, which
 * caused the missing-locale warning to fire repeatedly when parent components
 * recreated the `values` prop. Centralising the lookup here keeps the warning
 * tied to state transitions (effect) rather than render frequency.
 */
export const useLocalisation = (
   
  localisations: any,
  currentLocale: string,
  logger?: {
    warning: (message: string, ctx?: Record<string, unknown>) => void;
  },
  key?: string,
) => {
  const localisation = useMemo(
    () => findLocalisation(localisations, currentLocale),
    [localisations, currentLocale],
  );

  useEffect(() => {
    if (!localisation && logger) {
      logger.warning(DATADOG_ACTIONS.websheet.localisation_not_found);
    }
  }, [localisation, logger]);

  return useMemo(
    () => (key ? localisation?.[key] : localisation),
    [localisation, key],
  );
};
